import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { NgButton, NgInputDate } from '@/ui';
import { useEditForm } from '@/ui/forms/useEditForm';

import type { IncidentArchiveFilters } from './filters';
import { defaultIncidentArchiveFilters, sanitizeFilters } from './filters';
import { IncidentArchiveFiltersSummary } from './IncidentArchiveFiltersSummary';

export type IncidentArchiveActionBoxProps = {
  filters?: IncidentArchiveFilters | null;
  filtersApplied: (filters: IncidentArchiveFilters) => void | Promise<void>;
};

export function IncidentArchiveActionBox({ filters, filtersApplied }: IncidentArchiveActionBoxProps) {
  const { t } = useTranslation();

  const [filtersModel, setFiltersModel] = useState<IncidentArchiveFilters>(
    () => filters ?? defaultIncidentArchiveFilters(),
  );
  useEffect(() => {
    setFiltersModel(filters ?? defaultIncidentArchiveFilters());
  }, [filters]);

  const editForm = useEditForm<'phoneNumber' | 'callerName' | 'note'>({
    initialBaselineValues: {
      phoneNumber: filtersModel.phoneNumber ?? '',
      callerName: filtersModel.callerName ?? '',
      note: filtersModel.note ?? '',
    },
  });

  const [expandedFilters, setExpandedFilters] = useState(false);

  const effectiveDisplay = expandedFilters
    ? 'd-block position-absolute top-0 start-0 end-0 bg-white z-1'
    : 'd-none d-tablet-block';

  const applyFilters = () => {
    if (expandedFilters) {
      setExpandedFilters(false);
    }

    const sanitized = sanitizeFilters(filtersModel);
    setFiltersModel(sanitized);
    void filtersApplied(sanitized);
  };

  const expandFilters = () => setExpandedFilters((value) => !value);
  const closeFilters = () => setExpandedFilters(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    editForm.validate(() => ({}));
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="position-relative">
        <div
          className="d-flex d-tablet-none align-items-center justify-content-between gap-2 p-2 bg-light"
          onClick={expandFilters}
        >
          <i className="fal fa-filter"></i>

          <IncidentArchiveFiltersSummary filters={filtersModel} />

          <i className="fal fa-chevron-down"></i>
        </div>

        <div
          className={`action-box-backdrop bg-black bg-opacity-25 position-fixed top-0 start-0 end-0 bottom-0 z-0 ${expandedFilters ? 'd-block d-tablet-none' : 'd-none'}`}
          onClick={closeFilters}
        ></div>

        <div className={`${effectiveDisplay} p-3 p-tablet-0`}>
          <div className="d-flex gap-2 flex-column flex-tablet-row align-items-stretch align-items-tablet-end">
            <NgInputDate
              value={filtersModel.from}
              onValueChanged={(date) => setFiltersModel((model) => ({ ...model, from: date }))}
              label={t('IncidentArchive_Filters_DateFrom')}
              maxDate={filtersModel.to ?? undefined}
            />

            <NgInputDate
              value={filtersModel.to}
              onValueChanged={(date) => setFiltersModel((model) => ({ ...model, to: date }))}
              label={t('IncidentArchive_Filters_DateTo')}
              minDate={filtersModel.from ?? undefined}
            />

            <div>
              <label htmlFor="filterPhoneNumber" className="form-label">
                {t('IncidentArchive_Filters_PhoneNumber')}
              </label>
              <input
                value={filtersModel.phoneNumber ?? ''}
                onChange={(event) => {
                  setFiltersModel((model) => ({ ...model, phoneNumber: event.target.value }));
                  editForm.notifyFieldInput('phoneNumber', event.target.value);
                }}
                onBlur={() => editForm.notifyFieldBlur('phoneNumber')}
                id="filterPhoneNumber"
                className={editForm.fieldCssClass('phoneNumber', 'form-control')}
              />
            </div>

            <div>
              <label htmlFor="filterCallerName" className="form-label">
                {t('IncidentArchive_Filters_CallerName')}
              </label>
              <input
                value={filtersModel.callerName ?? ''}
                onChange={(event) => {
                  setFiltersModel((model) => ({ ...model, callerName: event.target.value }));
                  editForm.notifyFieldInput('callerName', event.target.value);
                }}
                onBlur={() => editForm.notifyFieldBlur('callerName')}
                id="filterCallerName"
                className={editForm.fieldCssClass('callerName', 'form-control')}
              />
            </div>

            <div>
              <label htmlFor="filterNote" className="form-label">
                {t('IncidentArchive_Filters_DispatcherNote')}
              </label>
              <input
                value={filtersModel.note ?? ''}
                onChange={(event) => {
                  setFiltersModel((model) => ({ ...model, note: event.target.value }));
                  editForm.notifyFieldInput('note', event.target.value);
                }}
                onBlur={() => editForm.notifyFieldBlur('note')}
                id="filterNote"
                className={editForm.fieldCssClass('note', 'form-control')}
              />
            </div>

            <NgButton
              submitButton
              text={t('IncidentArchive_Filters_FilterButtonLabel')}
              onClick={applyFilters}
            />

            <NgButton
              icon="fal fa-chevron-up"
              type="link"
              size="small"
              onClick={closeFilters}
              cssClass="d-tablet-none"
            />
          </div>
        </div>
      </div>
    </form>
  );
}
